import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { notify } from "../ui/notify";
import {
  Upload,
  Button,
  Select,
  Input,
} from "antd";
import dayjs from "dayjs";
import ScrollDatePicker from "../calendar/ScrollDatePicker";

import {
  actionJuntaEtapaGet,
  actionJuntaEtapaUpsert,
  actionJuntaEtapaUploadDoc,
  actionJuntaEtapaDeleteDoc,
  actionJuntaAgendaCreate,
  actionJuntaAgendaDelete,
  actionJuntaPruebasRegistroGet,
  actionJuntaPruebasRegistroCreate,
  actionJuntaPruebasRegistroUpdate,
  actionJuntaPruebasRegistroDelete,
} from "../../redux/actions/junta/junta";
import { PATH } from "../../redux/utils";

import "./EtapaProcesalOrdinario.css";

export const ETAPAS_JUNTA = [
  { n: 1, icon: "post_add",          title: "Radicación\nde demanda" },
  { n: 2, icon: "gavel",             title: "Audiencia CDE" },
  { n: 3, icon: "checklist",         title: "Audiencia OAP" },
  { n: 4, icon: "pending_actions",   title: "Acuerdo / Reserva\nsobre admisión\nde pruebas" },
  { n: 5, icon: "fact_check",        title: "Desahogo\nde pruebas" },
  { n: 6, icon: "record_voice_over", title: "Alegatos" },
  { n: 7, icon: "hourglass_top",     title: "Reserva\npara Laudo" },
  { n: 8, icon: "editor_choice",     title: "Laudo\ndictado" },
  { n: 9, icon: "handyman",          title: "En ejecución" },
];

const RESULTADO_OPTIONS = [
  { value: "Celebrada", label: "Celebrada" },
  { value: "Diferida",  label: "Diferida" },
  { value: "Desierta",  label: "Desierta" },
];

// Etapa 3 = Audiencia OAP: sustituye "Celebrada" por las dos variantes de admisión
const RESULTADO_OPTIONS_OAP = [
  { value: "Celebrada y se reservó sobre la admisión", label: "Celebrada y se reservó sobre la admisión" },
  { value: "Celebrada y se dictó acuerdo sobre la admisión", label: "Celebrada y se dictó acuerdo sobre la admisión" },
  { value: "Diferida", label: "Diferida" },
  { value: "Desierta", label: "Desierta" },
];

const getResultadoOptions = (etapa) => (etapa === 3 ? RESULTADO_OPTIONS_OAP : RESULTADO_OPTIONS);

// Catálogo de "Tipo de prueba" del registro Ofrecidas/Admitidas — copiado
// tal cual del mockup de referencia (Expediente Junta Principal.dc.html).
const TIPO_PRUEBA_OPTIONS = [
  "Confesional a cargo de la demandada",
  "Confesional a cargo del actor",
  "Confesional por hechos propios",
  "Testimonial",
  "Documental pública",
  "Documental privada",
  "Prueba de informes",
  "Inspección",
  "Pericial",
  "Ratificación, objeción y tacha",
  "Presuncional legal y humana",
  "Instrumental de actuaciones",
  "Fotografías y elementos de la ciencia",
].map((t) => ({ value: t, label: t }));

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// Tarjeta genérica de "etiqueta / valor" en grid — usada en la etapa 1
// (Radicación de demanda) para mostrar datos ya capturados del expediente.
function FieldGridCard({ icon, title, fields }) {
  return (
    <div className="lxp-ep-c10-card" style={{ marginBottom: 16 }}>
      <div className="lxp-ep-c10-card-header">
        <span className="material-symbols-outlined">{icon}</span>
        {title}
      </div>
      <div className="lxp-ep-c10-card-body">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
            gap: "16px 24px",
          }}
        >
          {fields.map(([label, value]) => (
            <div key={label}>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                  color: "#94a3b8",
                  marginBottom: 4,
                }}
              >
                {label}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#14306b" }}>
                {value || "—"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const PASO_DEFS = [
  { key: "ofrecida", n: "1", titulo: "OFRECIDAS", pie: "Ofrecimiento — art. 880", color: "#DC2626" },
  { key: "admitida", n: "2", titulo: "ADMITIDAS", pie: "Auto de admisión — art. 883", color: "#16a34a" },
];

const isCelebrada = (resultado) => !!resultado && resultado.startsWith("Celebrada");

const ORDINALS = ["1ra","2da","3ra","4ta","5ta","6ta","7ma","8va","9na","10ma"];
const ordinal = (n) => ORDINALS[n] ?? `${n + 1}°`;

const MESES_ES = ['ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'];
const fmtFechaLargaHora = (v) => `${v.format("DD")} DE ${MESES_ES[v.month()]} DE ${v.format("YYYY")} A LAS ${v.format("HH:mm")} HORAS.`;

const getExt = (name = "") => {
  const s = String(name);
  const i = s.lastIndexOf(".");
  return i >= 0 ? s.slice(i + 1).toLowerCase() : "";
};

const etapaIsDone = (data) => {
  if (!data || !data.items) return false;
  return data.items.some(
    (item) =>
      item?.fecha_audiencia ||
      item?.resultado ||
      (Array.isArray(item?.documentos) && item.documentos.length > 0),
  );
};

// Construye la lista ordenada de slots a mostrar como pestañas.
// Reglas:
//   1. Sin registros → un slot pendiente (1ra Activa).
//   2. Se recorren los registros en orden. Si alguno tiene resultado "Celebrada"
//      o "Desierta", se incluye ese slot y se CORTA la cadena: los registros
//      posteriores se ocultan (no se borran de la BD).
//   3. Si se llega al final sin haber cortado y el último tiene resultado
//      "Diferida", se añade un slot pendiente para la siguiente audiencia.
function deriveSlots(items) {
  if (!items || items.length === 0) {
    return [{ audiencia_diferida: 0, item: null, isPending: true }];
  }
  const sorted = [...items].sort((a, b) => a.audiencia_diferida - b.audiencia_diferida);

  const slots = [];
  for (const item of sorted) {
    slots.push({ audiencia_diferida: item.audiencia_diferida, item, isPending: false });
    if (item.resultado && item.resultado !== "Diferida") {
      return slots; // cadena cerrada: Celebrada o Desierta — no mostrar más
    }
  }

  // Todos los registros visibles son "Diferida" → añadir slot pendiente
  const last = slots[slots.length - 1];
  if (last.item?.resultado === "Diferida") {
    slots.push({
      audiencia_diferida: last.audiencia_diferida + 1,
      item: null,
      isPending: true,
    });
  }
  return slots;
}

// ── Sección de audiencia (pestañas de audiencia diferida + fecha/resultado
// + documentos) para una etapa (2–9). Se extrajo como componente aparte para
// poder montar DOS instancias independientes (CDE y OAP) cuando el expediente
// es "antes de la reforma 2012" y ambas audiencias se fusionan en un solo
// paso del timeline — cada una necesita su propio estado de slot/fecha/
// resultado, ya que se muestran al mismo tiempo en pantalla.
function AudienciaEtapaSection({
  idJunta,
  numEtapa,
  data,
  onReload,
  actorName,
  empresaName,
  expediente,
  onFechaAudienciaChanged,
  showDatesRow,
  sectionTitle,
}) {
  const dispatch = useDispatch();

  const [activeSlotIdx, setActiveSlotIdx] = useState(0);
  const [fechaAud, setFechaAud]           = useState(null);
  const [resultado, setResultado]         = useState(null);
  const [notas, setNotas]                 = useState("");
  const [etapaId, setEtapaId]             = useState(null);
  const [editFecha, setEditFecha]         = useState(true);
  const [editResult, setEditResult]       = useState(true);
  const [loadingFecha, setLoadingFecha]   = useState(false);
  const [loadingResult, setLoadingResult] = useState(false);
  const [loadingDocs, setLoadingDocs]     = useState(false);

  const allItems = data?.items || [];
  const slots = useMemo(() => deriveSlots(allItems), [allItems]);
  const safeIdx = Math.min(activeSlotIdx, Math.max(0, slots.length - 1));
  const activeSlot = slots[safeIdx] ?? null;

  useEffect(() => {
    setActiveSlotIdx(0);
  }, [numEtapa]);

  useEffect(() => {
    if (!activeSlot) return;
    const item = activeSlot.item;
    if (item) {
      setEtapaId(item.id_junta_etapa ?? null);
      setResultado(item.resultado || null);
      setNotas(item.notas || "");
      if (item.fecha_audiencia) {
        const d = dayjs(item.fecha_audiencia);
        if (d.isValid()) {
          setFechaAud(d);
          setEditFecha(false);
        } else {
          setFechaAud(null);
          setEditFecha(true);
        }
      } else {
        setFechaAud(null);
        setEditFecha(true);
      }
      setEditResult(!item.resultado);
    } else {
      setEtapaId(null);
      setFechaAud(null);
      setResultado(null);
      setNotas("");
      setEditFecha(true);
      setEditResult(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeIdx, data]);

  const handleSaveFecha = async () => {
    if (!fechaAud) {
      notify.error({ message: "Selecciona la fecha de la audiencia" });
      return;
    }
    if (!activeSlot) return;
    try {
      setLoadingFecha(true);

      const oldAgendaId = activeSlot.item?.id_agenda;
      if (oldAgendaId) {
        try { await dispatch(actionJuntaAgendaDelete(oldAgendaId)); } catch { /* silencioso */ }
      }

      const title = `${(actorName || "").toUpperCase()} VS ${(empresaName || "").toUpperCase()}`.slice(0, 125);
      const startAt = fechaAud.toISOString();
      const endAt   = fechaAud.add(1, "hour").toISOString();
      let newAgendaId = null;
      try {
        const agendaRes = await dispatch(actionJuntaAgendaCreate({
          title,
          description: `Expediente: ${expediente}`,
          start_at: startAt,
          end_at: endAt,
        }));
        newAgendaId = agendaRes?.id ?? agendaRes?.id_agenda ?? null;
      } catch { /* si falla la agenda, continuamos con el guardado */ }

      await dispatch(actionJuntaEtapaUpsert(idJunta, numEtapa, {
        fecha_audiencia: fechaAud.format("YYYY-MM-DD HH:mm:00"),
        resultado,
        notas,
        audiencia_diferida: activeSlot.audiencia_diferida,
        id_agenda: newAgendaId,
      }));

      notify.success({ message: "Fecha guardada correctamente" });
      await onReload();
      setEditFecha(false);
      onFechaAudienciaChanged?.();
    } catch {
      notify.error({ message: "Error al guardar fecha" });
    } finally {
      setLoadingFecha(false);
    }
  };

  const handleDeleteFecha = async () => {
    if (!activeSlot) return;
    try {
      setLoadingFecha(true);

      const oldAgendaId = activeSlot.item?.id_agenda;
      if (oldAgendaId) {
        try { await dispatch(actionJuntaAgendaDelete(oldAgendaId)); } catch { /* silencioso */ }
      }

      await dispatch(actionJuntaEtapaUpsert(idJunta, numEtapa, {
        fecha_audiencia: null,
        resultado,
        notas,
        audiencia_diferida: activeSlot.audiencia_diferida,
        id_agenda: null,
      }));

      notify.success({ message: "Fecha eliminada" });
      setFechaAud(null);
      setEditFecha(true);
      await onReload();
      onFechaAudienciaChanged?.();
    } catch {
      notify.error({ message: "Error al eliminar fecha" });
    } finally {
      setLoadingFecha(false);
    }
  };

  const handleSaveResult = async () => {
    if (!resultado) {
      notify.error({ message: "El resultado es obligatorio" });
      return;
    }
    if (!activeSlot) return;
    try {
      setLoadingResult(true);
      await dispatch(actionJuntaEtapaUpsert(idJunta, numEtapa, {
        fecha_audiencia: fechaAud ? fechaAud.format("YYYY-MM-DD HH:mm:00") : null,
        resultado,
        notas,
        audiencia_diferida: activeSlot.audiencia_diferida,
      }));
      notify.success({ message: "Resultado guardado correctamente" });
      await onReload();
    } catch {
      notify.error({ message: "Error al guardar resultado" });
    } finally {
      setLoadingResult(false);
    }
  };

  const handleUploadDoc = async (file) => {
    if (!activeSlot) return false;
    try {
      setLoadingDocs(true);
      await dispatch(
        actionJuntaEtapaUploadDoc(
          idJunta,
          numEtapa,
          activeSlot.audiencia_diferida,
          file,
        ),
      );
      notify.success({ message: "Documento cargado" });
      await onReload();
    } catch {
      notify.error({ message: "Error al subir documento" });
    } finally {
      setLoadingDocs(false);
    }
    return false;
  };

  const handleDeleteDoc = async (doc) => {
    try {
      await dispatch(
        actionJuntaEtapaDeleteDoc(
          idJunta,
          numEtapa,
          doc.id_junta_etapa_doc || doc.id,
        ),
      );
      notify.success({ message: "Documento eliminado" });
      await onReload();
    } catch {
      notify.error({ message: "Error al eliminar documento" });
    }
  };

  const documentos = Array.isArray(activeSlot?.item?.documentos)
    ? activeSlot.item.documentos
    : [];

  return (
    <div className="lxp-ep-audiencia-section" style={{ marginBottom: 24 }}>
      {sectionTitle && (
        <h4 style={{ margin: "0 0 10px", fontSize: 11.5, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: "#0A539A" }}>
          {sectionTitle}
        </h4>
      )}

      {/* Pestañas de audiencias — solo visibles cuando hay más de una */}
      {slots.length > 1 && (
        <div className="lxp-ep-diferida-tabs">
          {slots.map((slot, pos) => {
            const item = slot.item;
            const isActive = pos === safeIdx;
            const icon = slot.isPending
              ? "add_circle"
              : item?.resultado === "Diferida"  ? "update"
              : isCelebrada(item?.resultado)    ? "check_circle"
              : item?.resultado === "Desierta"  ? "cancel"
              : "radio_button_unchecked";

            return (
              <button
                key={slot.audiencia_diferida}
                type="button"
                className={`lxp-ep-diferida-tab${isActive ? " active" : ""}`}
                onClick={() => setActiveSlotIdx(pos)}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
                  {icon}
                </span>
                {ordinal(pos)} Audiencia
                {!slot.isPending && item?.resultado && (
                  <span
                    className={[
                      "lxp-ep-diferida-badge",
                      isCelebrada(item.resultado)    ? "lxp-ep-diferida-badge--celebrada" : "",
                      item.resultado === "Desierta"  ? "lxp-ep-diferida-badge--desierta"  : "",
                    ].join(" ").trim()}
                  >
                    {item.resultado}
                  </span>
                )}
                {slot.isPending && (
                  <span className="lxp-ep-diferida-badge lxp-ep-diferida-badge--new">
                    Activa
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Cards antes / después de la audiencia ─────────────────────── */}
      {showDatesRow && (
      <div className="lxp-ep-dates-row">
        <div className="lxp-ep-c10-twocol">

          {/* Card: Antes de la audiencia (fecha) */}
          <div className="lxp-ep-c10-card">
            <div className="lxp-ep-c10-card-header">
              <span className="material-symbols-outlined">event</span>
              Antes de la audiencia
            </div>
            <div className="lxp-ep-c10-card-body">
              {editFecha ? (
                <>
                  <div className="lxp-ep-datefield">
                    <div className="lxp-ep-datelabel">Fecha y Hora</div>
                    <ScrollDatePicker
                      showTime={{ format: "HH:mm" }}
                      needConfirm={false}
                      value={fechaAud}
                      onChange={(v) => setFechaAud(v)}
                      format={fmtFechaLargaHora}
                      placeholder="dd/mm/yyyy hh:mm"
                      className="lxp-ep-datepicker"
                      allowClear
                    />
                  </div>
                  <div className="lxp-ep-c10-card-footer">
                    {etapaId && (
                      <button
                        className="lxp-ep-c10-cancel-btn"
                        onClick={() => setEditFecha(false)}
                      >
                        Cancelar
                      </button>
                    )}
                    {etapaId && fechaAud && (
                      <button
                        className="lxp-ep-c10-cancel-btn"
                        style={{ color: "#dc2626", borderColor: "#fecaca" }}
                        onClick={handleDeleteFecha}
                        disabled={loadingFecha}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 15 }}>
                          delete
                        </span>
                        Eliminar fecha
                      </button>
                    )}
                    <Button
                      className="custom-button"
                      loading={loadingFecha}
                      onClick={handleSaveFecha}
                      size="middle"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 15 }}>
                        save
                      </span>
                      Guardar fecha
                    </Button>
                  </div>
                </>
              ) : (
                <div className="lxp-ep-c10-view">
                  <div className="lxp-ep-c10-view-row">
                    <span className="material-symbols-outlined lxp-ep-c10-view-icon">
                      calendar_today
                    </span>
                    <div>
                      <div className="lxp-ep-c10-view-label">Fecha y Hora</div>
                      <div className="lxp-ep-c10-view-value">
                        {fechaAud
                          ? fechaAud.format("DD [de] MMMM [de] YYYY [—] HH:mm [hrs]")
                          : "—"}
                      </div>
                    </div>
                  </div>
                  <div className="lxp-ep-c10-view-actions">
                    <button
                      type="button"
                      className="lxp-ep-c10-edit-btn lxp-ep-c10-edit-btn--inline"
                      onClick={() => setEditFecha(true)}
                    >
                      <span className="material-symbols-outlined">edit</span>
                      Editar
                    </button>
                    {fechaAud && (
                      <button
                        type="button"
                        className="lxp-ep-c10-cancel-btn"
                        style={{ color: "#dc2626", borderColor: "#fecaca" }}
                        onClick={handleDeleteFecha}
                        disabled={loadingFecha}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 15 }}>
                          delete
                        </span>
                        Eliminar fecha
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Card: Después de la audiencia */}
          <div className="lxp-ep-c10-card">
            <div className="lxp-ep-c10-card-header">
              <span className="material-symbols-outlined">task_alt</span>
              Después de la audiencia
              {!editResult && (
                <button
                  className="lxp-ep-c10-edit-btn"
                  onClick={() => setEditResult(true)}
                >
                  <span className="material-symbols-outlined">edit</span>
                  Editar
                </button>
              )}
            </div>
            <div className="lxp-ep-c10-card-body">
              {editResult ? (
                <>
                  <div className="lxp-ep-datefield">
                    <div className="lxp-ep-datelabel">
                      Resultado <span style={{ color: "#ef4444" }}>*</span>
                    </div>
                    <Select
                      value={resultado}
                      onChange={(v) => setResultado(v)}
                      placeholder="Selecciona resultado"
                      style={{ width: "100%" }}
                      options={getResultadoOptions(numEtapa)}
                    />
                  </div>
                  <div className="lxp-ep-datefield" style={{ flex: 1 }}>
                    <div className="lxp-ep-datelabel">Notas</div>
                    <textarea
                      className="lxp-ep-textarea"
                      value={notas}
                      onChange={(e) => setNotas(e.target.value)}
                      placeholder="Describe lo que ocurrió en la audiencia..."
                      rows={3}
                      style={{ minHeight: 72 }}
                    />
                  </div>
                  <div className="lxp-ep-c10-card-footer">
                    {etapaId && (
                      <button
                        className="lxp-ep-c10-cancel-btn"
                        onClick={() => setEditResult(false)}
                      >
                        Cancelar
                      </button>
                    )}
                    <Button
                      className="custom-button"
                      loading={loadingResult}
                      onClick={handleSaveResult}
                      size="middle"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 15 }}>
                        save
                      </span>
                      Guardar resultado
                    </Button>
                  </div>
                </>
              ) : (
                <div className="lxp-ep-c10-view">
                  <div className="lxp-ep-c10-view-row">
                    <span className="material-symbols-outlined lxp-ep-c10-view-icon">
                      {isCelebrada(resultado)
                        ? "check_circle"
                        : resultado === "Diferida"
                        ? "update"
                        : resultado === "Desierta"
                        ? "cancel"
                        : "help_outline"}
                    </span>
                    <div>
                      <div className="lxp-ep-c10-view-label">Resultado</div>
                      <div
                        className={`lxp-ep-c10-resultado-badge lxp-ep-c10-resultado--${(
                          resultado || ""
                        ).toLowerCase()}`}
                      >
                        {resultado || "—"}
                      </div>
                    </div>
                  </div>
                  {notas ? (
                    <div className="lxp-ep-c10-view-row lxp-ep-c10-view-row--notas">
                      <span className="material-symbols-outlined lxp-ep-c10-view-icon">
                        notes
                      </span>
                      <div>
                        <div className="lxp-ep-c10-view-label">Notas</div>
                        <div className="lxp-ep-c10-view-notas">{notas}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="lxp-ep-c10-view-row">
                      <span className="material-symbols-outlined lxp-ep-c10-view-icon">
                        notes
                      </span>
                      <div>
                        <div className="lxp-ep-c10-view-label">Notas</div>
                        <div className="lxp-ep-c10-view-value">Sin notas</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* ── Documentos ─────────────────────────────────────────────────── */}
      <div className="lxp-ep-c10-docs-wrap">
        <div className="lxp-ep-docs">
          <div className="lxp-ep-docs-head">
            <div className="lxp-ep-docs-title">
              <span className="material-symbols-outlined">folder_data</span>
              Documentos de la Audiencia
            </div>
            <div className="lxp-ep-docs-right">
              <span className="lxp-ep-docs-count">{documentos.length} archivos</span>
              <Upload beforeUpload={handleUploadDoc} showUploadList={false} multiple>
                <button className="lxp-ep-link" type="button">
                  <span className="material-symbols-outlined">add</span>
                  Agregar
                </button>
              </Upload>
            </div>
          </div>
          <div className="lxp-ep-docs-body">
            {documentos.map((d) => {
              const ext   = getExt(d?.path || d?.filename || "");
              const isPdf = ext === "pdf";
              return (
                <div
                  key={d.id_junta_etapa_doc || d.id}
                  className={`lxp-ep-doc-item ${isPdf ? "is-pdf" : "is-doc"}`}
                >
                  <div className="lxp-ep-doc-left">
                    <div className="lxp-ep-doc-ico">
                      <span className="material-symbols-outlined">
                        {isPdf ? "picture_as_pdf" : "description"}
                      </span>
                    </div>
                    <div className="lxp-ep-doc-meta">
                      <div className="lxp-ep-doc-name">{d?.filename || "—"}</div>
                      <div className="lxp-ep-doc-sub">
                        <span className="material-symbols-outlined">schedule</span>{" "}
                        {d?.datetime
                          ? String(d.datetime).replace("T", " ").slice(0, 16)
                          : "—"}
                      </div>
                    </div>
                  </div>
                  <div className="lxp-ep-doc-actions">
                    {d?.url && (
                      <button
                        className="lxp-ep-iconbtn"
                        type="button"
                        onClick={() => window.open(`${PATH}${d.url}`, "_blank")}
                      >
                        <span className="material-symbols-outlined">visibility</span>
                      </button>
                    )}
                    <button
                      className="lxp-ep-iconbtn"
                      type="button"
                      onClick={() => handleDeleteDoc(d)}
                    >
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                </div>
              );
            })}
            <Upload
              beforeUpload={handleUploadDoc}
              showUploadList={false}
              multiple
              className="lxp-ep-upload-block"
            >
              <div className={`lxp-ep-drop ${documentos.length > 0 ? "is-compact" : ""}`}>
                <div className="lxp-ep-drop-ico">
                  <span className="material-symbols-outlined">cloud_upload</span>
                </div>
                <div className="lxp-ep-drop-t">Haga clic para cargar</div>
                <div className="lxp-ep-drop-s">o arrastre y suelte (PDF, Word)</div>
              </div>
            </Upload>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EtapaProcesalJunta({
  idJunta,
  actorName = "",
  empresaName = "",
  expediente = "",
  actoresList = [],
  demandadosList = [],
  todasRazonesOptions = [],
  handleSavePartesJuicio,
  datosExpediente = null,
  onFechaAudienciaChanged,
  regimenReforma2012 = "",
}) {
  // "antes de la reforma 2012": Audiencia CDE (etapa 2) y Audiencia OAP
  // (etapa 3) se fusionan en un solo paso del timeline ("Audiencia CDE /
  // Audiencia OAP"). Los datos siguen guardándose por separado (num_etapa
  // 2 y 3 en la BD) — solo cambia la presentación: al entrar a ese paso se
  // muestran ambas secciones apiladas.
  const mergeCdeOap = regimenReforma2012 === "antes";
  const dispatch = useDispatch();

  // Pestaña activa dentro de "Partes del Juicio" (actora | demandada)
  const [partesTab, setPartesTab] = useState("actora");
  // Sub-pestaña seleccionada dentro del tab maestro (nombre del actor, o
  // id_razon_social —como texto— de la demandada seleccionada)
  const [selectedLitigante, setSelectedLitigante] = useState(null);

  // Mantiene selectedLitigante válido cuando cambia el tab maestro o la
  // lista de partes (por default selecciona la primera disponible).
  useEffect(() => {
    const list = partesTab === "actora"
      ? actoresList
      : demandadosList.map((d) => (d.id_razon_social != null ? String(d.id_razon_social) : d.nombre));
    if (!list.length) {
      setSelectedLitigante(null);
      return;
    }
    if (!list.includes(selectedLitigante)) {
      setSelectedLitigante(list[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partesTab, actoresList, demandadosList]);

  // ── Registro de pruebas Ofrecidas/Admitidas ──────────────────────────
  const [pruebasRegistro, setPruebasRegistro] = useState([]);
  const [loadingPruebasRegistro, setLoadingPruebasRegistro] = useState(false);
  // Item en edición: { id|null, parte, estatus, tipo_prueba, objeto, fecha, observacion, isNew }
  const [editingItem, setEditingItem] = useState(null);
  const [savingItem, setSavingItem] = useState(false);

  const loadPruebasRegistro = useCallback(async () => {
    if (!idJunta) return;
    try {
      setLoadingPruebasRegistro(true);
      const res = await dispatch(actionJuntaPruebasRegistroGet(idJunta));
      setPruebasRegistro(res?.items || []);
    } catch {
      setPruebasRegistro([]);
    } finally {
      setLoadingPruebasRegistro(false);
    }
  }, [dispatch, idJunta]);

  useEffect(() => {
    loadPruebasRegistro();
  }, [loadPruebasRegistro]);

  const itemAdd = (parte, estatus, litigante) => {
    setEditingItem({
      id: null, parte, litigante, estatus, tipo_prueba: TIPO_PRUEBA_OPTIONS[0].value,
      objeto: "", fecha: null, observacion: "", isNew: true,
    });
  };

  const itemEdit = (item) => {
    setEditingItem({
      id: item.id_junta_prueba_registro, parte: item.parte, litigante: item.litigante,
      estatus: item.estatus, tipo_prueba: item.tipo_prueba, objeto: item.objeto || "",
      fecha: item.fecha || null, observacion: item.observacion || "", isNew: false,
    });
  };

  const itemCancel = () => setEditingItem(null);

  const itemSave = async () => {
    if (!editingItem) return;
    try {
      setSavingItem(true);
      const payload = {
        parte: editingItem.parte,
        litigante: editingItem.litigante ?? null,
        estatus: editingItem.estatus,
        tipo_prueba: editingItem.tipo_prueba,
        objeto: editingItem.objeto || null,
        fecha: editingItem.fecha || null,
        observacion: editingItem.observacion || null,
      };
      if (editingItem.isNew) {
        await dispatch(actionJuntaPruebasRegistroCreate(idJunta, payload));
      } else {
        await dispatch(actionJuntaPruebasRegistroUpdate(idJunta, editingItem.id, payload));
      }
      await loadPruebasRegistro();
      setEditingItem(null);
      notify.success({ message: "Prueba guardada", placement: "bottomRight" });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar la prueba",
        description: e?.response?.data?.detail || e?.message,
        placement: "bottomRight",
      });
    } finally {
      setSavingItem(false);
    }
  };

  const itemDelete = async () => {
    if (!editingItem?.id) { setEditingItem(null); return; }
    try {
      setSavingItem(true);
      await dispatch(actionJuntaPruebasRegistroDelete(idJunta, editingItem.id));
      await loadPruebasRegistro();
      setEditingItem(null);
      notify.success({ message: "Prueba eliminada", placement: "bottomRight" });
    } catch (e) {
      notify.error({
        message: "No se pudo eliminar",
        description: e?.response?.data?.detail || e?.message,
        placement: "bottomRight",
      });
    } finally {
      setSavingItem(false);
    }
  };

  // ── Administrar partes — lista única (actora + demandada mezcladas),
  // igual que el mockup: cada renglón tiene su propio Carácter editable.
  // { _id, tipo: 'actora'|'demandada', nombre, id_razon_social }
  const [litOpen, setLitOpen] = useState(false);
  const [litRows, setLitRows] = useState([]);
  const [savingPartes, setSavingPartes] = useState(false);
  const litRowSeq = React.useRef(0);
  const nextLitId = () => `lit_${++litRowSeq.current}`;

  const litToggleOpen = () => {
    if (!litOpen) {
      const rows = [
        ...actoresList.map((nombre) => ({ _id: nextLitId(), tipo: "actora", nombre, id_razon_social: null })),
        ...demandadosList.map((d) => ({
          _id: nextLitId(), tipo: "demandada", nombre: d.nombre,
          id_razon_social: d.id_razon_social != null ? String(d.id_razon_social) : null,
        })),
      ];
      setLitRows(rows.length ? rows : [{ _id: nextLitId(), tipo: "actora", nombre: "", id_razon_social: null }]);
    }
    setLitOpen((v) => !v);
  };

  const litAddRow = () => {
    setLitRows((rows) => [...rows, { _id: nextLitId(), tipo: "demandada", nombre: "", id_razon_social: null }]);
  };

  const litRemoveRow = (id) => {
    setLitRows((rows) => rows.filter((r) => r._id !== id));
  };

  const litSetRow = (id, patch) => {
    setLitRows((rows) => rows.map((r) => (r._id === id ? { ...r, ...patch } : r)));
  };

  const litSave = async () => {
    try {
      setSavingPartes(true);
      const actoresArr = litRows.filter((r) => r.tipo === "actora").map((r) => r.nombre);
      const razonesIds = litRows.filter((r) => r.tipo === "demandada" && r.id_razon_social).map((r) => r.id_razon_social);
      await handleSavePartesJuicio?.(actoresArr, razonesIds);
      setLitOpen(false);
    } finally {
      setSavingPartes(false);
    }
  };

  // Etapa del procedimiento (1–8)
  const [etapaSeleccionada, setEtapaSeleccionada] = useState(1);

  // Índice de posición dentro del array de slots (0 = 1ra audiencia, 1 = 2da, …)
  const [activeSlotIdx, setActiveSlotIdx] = useState(0);

  // Caché de datos por etapa: { [num_etapa]: { items: [...] } | false }
  const [etapasData, setEtapasData] = useState({});

  // Estado del formulario de la audiencia activa (usado solo por la etapa 1,
  // "Fecha para audiencia de CDE" — las etapas 2-9 usan su propio estado
  // dentro de <AudienciaEtapaSection>).
  const [fechaAud, setFechaAud]           = useState(null);
  const [resultado, setResultado]         = useState(null);
  const [notas, setNotas]                 = useState("");
  const [etapaId, setEtapaId]             = useState(null);
  const [editFecha, setEditFecha]         = useState(true);
  const [loadingFecha, setLoadingFecha]   = useState(false);

  const fetchEtapa = useCallback(async (num) => {
    try {
      const res = await dispatch(actionJuntaEtapaGet(idJunta, num));
      const items = res?.items || [];
      setEtapasData((prev) => ({ ...prev, [num]: { items } }));
      return items;
    } catch {
      setEtapasData((prev) => ({ ...prev, [num]: false }));
      return null;
    }
  }, [dispatch, idJunta]);

  // Cargar todas las etapas al montar
  useEffect(() => {
    if (!idJunta) return;
    ETAPAS_JUNTA.forEach((e) => fetchEtapa(e.n));
  }, [idJunta, fetchEtapa]);

  // Al cambiar de etapa, resetear al primer slot
  useEffect(() => {
    setActiveSlotIdx(0);
  }, [etapaSeleccionada]);

  // Datos y slots derivados de la etapa seleccionada
  const currentData = etapasData[etapaSeleccionada];
  const allItems    = currentData?.items || [];
  const slots = useMemo(() => deriveSlots(allItems), [allItems]);

  // Clamp del índice activo para evitar out-of-bounds cuando los slots cambian
  const safeIdx  = Math.min(activeSlotIdx, Math.max(0, slots.length - 1));
  const activeSlot = slots[safeIdx] ?? null;

  // Rellenar el formulario cada vez que cambia el slot activo o se recargan los datos
  useEffect(() => {
    if (!activeSlot) return;
    const item = activeSlot.item;
    if (item) {
      setEtapaId(item.id_junta_etapa ?? null);
      setResultado(item.resultado || null);
      setNotas(item.notas || "");
      if (item.fecha_audiencia) {
        const d = dayjs(item.fecha_audiencia);
        if (d.isValid()) {
          setFechaAud(d);
          setEditFecha(false);
        } else {
          setFechaAud(null);
          setEditFecha(true);
        }
      } else {
        setFechaAud(null);
        setEditFecha(true);
      }
    } else {
      // Slot pendiente: formulario vacío en modo edición
      setEtapaId(null);
      setFechaAud(null);
      setResultado(null);
      setNotas("");
      setEditFecha(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeIdx, currentData]);

  const reloadCurrent = useCallback(
    () => fetchEtapa(etapaSeleccionada),
    [fetchEtapa, etapaSeleccionada],
  );

  const handleSaveFecha = async () => {
    if (!fechaAud) {
      notify.error({ message: "Selecciona la fecha de la audiencia" });
      return;
    }
    if (!activeSlot) return;
    try {
      setLoadingFecha(true);

      // Borrar agenda anterior si ya existía
      const oldAgendaId = activeSlot.item?.id_agenda;
      if (oldAgendaId) {
        try { await dispatch(actionJuntaAgendaDelete(oldAgendaId)); } catch { /* silencioso */ }
      }

      // Crear nueva entrada en agenda
      const title = `${(actorName || "").toUpperCase()} VS ${(empresaName || "").toUpperCase()}`.slice(0, 125);
      const startAt = fechaAud.toISOString();
      const endAt   = fechaAud.add(1, "hour").toISOString();
      let newAgendaId = null;
      try {
        const agendaRes = await dispatch(actionJuntaAgendaCreate({
          title,
          description: `Expediente: ${expediente}`,
          start_at: startAt,
          end_at: endAt,
        }));
        newAgendaId = agendaRes?.id ?? agendaRes?.id_agenda ?? null;
      } catch { /* si falla la agenda, continuamos con el guardado */ }

      // Guardar etapa con id_agenda actualizado
      await dispatch(actionJuntaEtapaUpsert(idJunta, etapaSeleccionada, {
        fecha_audiencia: fechaAud.format("YYYY-MM-DD HH:mm:00"),
        resultado,
        notas,
        audiencia_diferida: activeSlot.audiencia_diferida,
        id_agenda: newAgendaId,
      }));

      notify.success({ message: "Fecha guardada correctamente" });
      await reloadCurrent();
      setEditFecha(false);
      onFechaAudienciaChanged?.();
    } catch {
      notify.error({ message: "Error al guardar fecha" });
    } finally {
      setLoadingFecha(false);
    }
  };

  const handleDeleteFecha = async () => {
    if (!activeSlot) return;
    try {
      setLoadingFecha(true);

      const oldAgendaId = activeSlot.item?.id_agenda;
      if (oldAgendaId) {
        try { await dispatch(actionJuntaAgendaDelete(oldAgendaId)); } catch { /* silencioso */ }
      }

      await dispatch(actionJuntaEtapaUpsert(idJunta, etapaSeleccionada, {
        fecha_audiencia: null,
        resultado,
        notas,
        audiencia_diferida: activeSlot.audiencia_diferida,
        id_agenda: null,
      }));

      notify.success({ message: "Fecha eliminada" });
      setFechaAud(null);
      setEditFecha(true);
      await reloadCurrent();
      onFechaAudienciaChanged?.();
    } catch {
      notify.error({ message: "Error al eliminar fecha" });
    } finally {
      setLoadingFecha(false);
    }
  };

  // Pasos del timeline: normalmente 1 a 1 con ETAPAS_JUNTA, pero si el
  // expediente es "antes de la reforma 2012" las etapas 2 (CDE) y 3 (OAP) se
  // fusionan visualmente en un solo paso — los datos de cada una se siguen
  // guardando por separado (num_etapa 2 y 3).
  const timelineSteps = useMemo(() => {
    if (!mergeCdeOap) {
      return ETAPAS_JUNTA.map((e) => ({ ...e, numEtapas: [e.n] }));
    }
    const out = [];
    for (const e of ETAPAS_JUNTA) {
      if (e.n === 3) continue; // se fusiona dentro del paso de la etapa 2
      if (e.n === 2) {
        out.push({
          n: 2,
          icon: "gavel",
          title: "Audiencia CDE / OAP",
          numEtapas: [2, 3],
        });
        continue;
      }
      out.push({ ...e, numEtapas: [e.n] });
    }
    return out;
  }, [mergeCdeOap]);

  const steps = useMemo(() =>
    timelineSteps.map((s, idx) => ({
      ...s,
      displayN: idx + 1,
      status: s.numEtapas.some((n) => etapaIsDone(etapasData[n])) ? "done" : "empty",
    })),
  [timelineSteps, etapasData]);

  const isMergedView = mergeCdeOap && etapaSeleccionada === 2;

  const currentStepTitle = useMemo(() => {
    const s = timelineSteps.find((st) => st.numEtapas.includes(etapaSeleccionada));
    return s?.title?.replace("\n", " ") || `ETAPA ${etapaSeleccionada}`;
  }, [timelineSteps, etapaSeleccionada]);

  return (
    <div className="lxp-ep-wrap">
      <div className="lxp-ep-card">
        <div className="lxp-ep-head">
          <div>
            <h2 className="lxp-ep-title">
              <span className="material-symbols-outlined">schema</span>
              Proceso de la Junta de Conciliación
            </h2>
            <p className="lxp-ep-sub">
              Etapas del procedimiento ante la Junta de Conciliación y Arbitraje.
            </p>
          </div>
        </div>

        {/* ── Timeline de etapas ─────────────────────────────────────────── */}
        <div className="lxp-ep-timeline-scroll">
          <div className="lxp-ep-timeline">
            <div className="lxp-ep-line" />
            <div className="lxp-ep-steps">
              {steps.map((s) => {
                const isSelected = s.numEtapas.includes(etapaSeleccionada);
                const isDone     = s.status === "done";
                const isEmpty    = s.status === "empty";
                return (
                  <div
                    key={s.n}
                    role="button"
                    tabIndex={0}
                    onClick={() => setEtapaSeleccionada(s.n)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") setEtapaSeleccionada(s.n);
                    }}
                    className={[
                      "lxp-ep-step",
                      "is-clickable",
                      isSelected ? "is-active" : "",
                      isDone     ? "is-done"   : "",
                      isEmpty    ? "is-empty"  : "",
                    ].join(" ").trim()}
                  >
                    <div className="lxp-ep-step-n">{s.displayN}</div>
                    <div className="lxp-ep-step-stick" />
                    <div className="lxp-ep-step-icon">
                      <span className="material-symbols-outlined">{s.icon}</span>
                    </div>
                    <div className={isSelected ? "lxp-ep-step-text is-active-text" : "lxp-ep-step-text"}>
                      {String(s.title).split("\n").map((line, idx) => (
                        <div key={idx}>{line}</div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Panel de detalle ───────────────────────────────────────────── */}
        <div className="lxp-ep-bottom" style={{ gridTemplateColumns: "1fr" }}>
          <div className="lxp-ep-left">
            <h3 className="lxp-ep-detail-head">
              <span className="lxp-ep-dot" />
              DETALLE: {currentStepTitle}
            </h3>

            {/* Pestañas de audiencias de la etapa 1 — solo visibles cuando hay
                más de una. Las etapas 2-9 tienen sus propias pestañas dentro
                de <AudienciaEtapaSection>. */}
            {etapaSeleccionada === 1 && slots.length > 1 && (
              <div className="lxp-ep-diferida-tabs">
                {slots.map((slot, pos) => {
                  const item = slot.item;
                  const isActive = pos === safeIdx;
                  const icon = slot.isPending
                    ? "add_circle"
                    : item?.resultado === "Diferida"  ? "update"
                    : isCelebrada(item?.resultado)    ? "check_circle"
                    : item?.resultado === "Desierta"  ? "cancel"
                    : "radio_button_unchecked";

                  return (
                    <button
                      key={slot.audiencia_diferida}
                      type="button"
                      className={`lxp-ep-diferida-tab${isActive ? " active" : ""}`}
                      onClick={() => setActiveSlotIdx(pos)}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
                        {icon}
                      </span>
                      {ordinal(pos)} Audiencia
                      {!slot.isPending && item?.resultado && (
                        <span
                          className={[
                            "lxp-ep-diferida-badge",
                            isCelebrada(item.resultado)    ? "lxp-ep-diferida-badge--celebrada" : "",
                            item.resultado === "Desierta"  ? "lxp-ep-diferida-badge--desierta"  : "",
                          ].join(" ").trim()}
                        >
                          {item.resultado}
                        </span>
                      )}
                      {slot.isPending && (
                        <span className="lxp-ep-diferida-badge lxp-ep-diferida-badge--new">
                          Activa
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* ── Etapas 2-9: panel(es) de audiencia (fecha/resultado/
                documentos). Cuando es "antes de la reforma 2012" y el paso
                seleccionado es el fusionado CDE/OAP, se muestran las dos
                secciones (etapa 2 y etapa 3) apiladas, cada una con su
                propio estado — los datos siguen guardándose por separado. ── */}
            {etapaSeleccionada !== 1 && (
              isMergedView ? (
                <>
                  <AudienciaEtapaSection
                    idJunta={idJunta}
                    numEtapa={2}
                    sectionTitle="Audiencia CDE"
                    data={etapasData[2]}
                    onReload={() => fetchEtapa(2)}
                    actorName={actorName}
                    empresaName={empresaName}
                    expediente={expediente}
                    onFechaAudienciaChanged={onFechaAudienciaChanged}
                    showDatesRow={false}
                  />
                  <AudienciaEtapaSection
                    idJunta={idJunta}
                    numEtapa={3}
                    sectionTitle="Audiencia OAP"
                    data={etapasData[3]}
                    onReload={() => fetchEtapa(3)}
                    actorName={actorName}
                    empresaName={empresaName}
                    expediente={expediente}
                    onFechaAudienciaChanged={onFechaAudienciaChanged}
                    showDatesRow
                  />
                </>
              ) : (
                <AudienciaEtapaSection
                  idJunta={idJunta}
                  numEtapa={etapaSeleccionada}
                  data={etapasData[etapaSeleccionada]}
                  onReload={() => fetchEtapa(etapaSeleccionada)}
                  actorName={actorName}
                  empresaName={empresaName}
                  expediente={expediente}
                  onFechaAudienciaChanged={onFechaAudienciaChanged}
                  showDatesRow={etapaSeleccionada !== 2}
                />
              )
            )}

            {/* ── Etapa 1: Fecha de la audiencia de CDE — arriba de todo,
                es lo primero que se debe notar al abrir la etapa. ─────── */}
            {etapaSeleccionada === 1 && (
              <div className="lxp-ep-c10-card" style={{ marginBottom: 16 }}>
                <div className="lxp-ep-c10-card-header">
                  <span className="material-symbols-outlined">event</span>
                  Fecha para audiencia de CDE
                </div>
                <div className="lxp-ep-c10-card-body">
                  {editFecha ? (
                    <>
                      <div className="lxp-ep-datefield">
                        <div className="lxp-ep-datelabel">Fecha y Hora</div>
                        <ScrollDatePicker
                          showTime={{ format: "HH:mm" }}
                          needConfirm={false}
                          value={fechaAud}
                          onChange={(v) => setFechaAud(v)}
                          format={fmtFechaLargaHora}
                          placeholder="dd/mm/yyyy hh:mm"
                          className="lxp-ep-datepicker"
                          allowClear
                        />
                      </div>
                      <div className="lxp-ep-c10-card-footer">
                        {etapaId && (
                          <button
                            className="lxp-ep-c10-cancel-btn"
                            onClick={() => setEditFecha(false)}
                          >
                            Cancelar
                          </button>
                        )}
                        {etapaId && fechaAud && (
                          <button
                            className="lxp-ep-c10-cancel-btn"
                            style={{ color: "#dc2626", borderColor: "#fecaca" }}
                            onClick={handleDeleteFecha}
                            disabled={loadingFecha}
                          >
                            Eliminar fecha
                          </button>
                        )}
                        <Button
                          className="custom-button"
                          loading={loadingFecha}
                          onClick={handleSaveFecha}
                          size="middle"
                        >
                          Guardar fecha
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="lxp-ep-c10-view">
                      <div>
                        <div className="lxp-ep-c10-view-label">Fecha y hora de la audiencia</div>
                        <div className="lxp-ep-c10-view-value lxp-ep-c10-view-value--lg">
                          {fechaAud
                            ? fechaAud.format("DD [de] MMMM [de] YYYY [—] HH:mm [hrs]")
                            : "Sin fecha asignada"}
                        </div>
                      </div>
                      <div className="lxp-ep-c10-view-actions">
                        <button
                          type="button"
                          className="lxp-ep-c10-edit-btn lxp-ep-c10-edit-btn--inline"
                          onClick={() => setEditFecha(true)}
                        >
                          Editar
                        </button>
                        {fechaAud && (
                          <button
                            type="button"
                            className="lxp-ep-c10-cancel-btn"
                            style={{ color: "#dc2626", borderColor: "#fecaca" }}
                            onClick={handleDeleteFecha}
                            disabled={loadingFecha}
                          >
                            Eliminar fecha
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Etapa 1: Datos del Expediente (no aplica resultado/
                documentos de audiencia en la radicación de la demanda) ─── */}
            {etapaSeleccionada === 1 && datosExpediente && (
              <>
                <FieldGridCard
                  icon="folder_open"
                  title="Datos del Expediente"
                  fields={[
                    ["Número de expediente", datosExpediente.numero],
                    ["Parte actora", datosExpediente.parteActora],
                    ["Demandada / Razón social", datosExpediente.demandada],
                    ["Ciudad", datosExpediente.ciudad],
                    ["Estado (entidad)", datosExpediente.estadoEntidad],
                    ["Autoridad", datosExpediente.autoridad],
                    ["Competencia", datosExpediente.competencia],
                    ["Tipo de procedimiento", datosExpediente.tipoProcedimiento],
                    ["Expediente(s) acumulado(s) o relacionado(s)", datosExpediente.relacionados],
                    ["Régimen (Reforma Laboral 2012)", datosExpediente.regimenReforma2012],
                  ]}
                />

                <FieldGridCard
                  icon="groups"
                  title="Partes y Representación"
                  fields={[
                    ["Parte(s) actora(s)", datosExpediente.partesActoras],
                    ["Demandado(s)", datosExpediente.demandados],
                    ["Tipo de asesoría de la actora", datosExpediente.tipoAsesoriaActora],
                    ["Abogado", datosExpediente.abogado],
                    ["Abogado responsable del despacho", datosExpediente.abogadoResponsable],
                  ]}
                />

                <FieldGridCard
                  icon="timeline"
                  title="Estado del Expediente al Darlo de Alta"
                  fields={[
                    ["Etapa procesal actual", datosExpediente.etapaProcesalInicial],
                    ["Acción intentada", datosExpediente.accionIntentada],
                    ["Fecha de presentación de la demanda", datosExpediente.fechaPresentacionDemanda],
                    ["Fecha de recepción en el despacho", datosExpediente.fechaRecepcion],
                    ["Fecha de emplazamiento", datosExpediente.fechaEmplazamiento],
                    ["Fecha de la última promoción o actuación", datosExpediente.fechaUltimaActuacion],
                    ["Próxima diligencia programada", datosExpediente.fechaProximaDiligencia],
                    ["Descripción de la próxima diligencia", datosExpediente.descripcionProximaDiligencia],
                  ]}
                />
              </>
            )}

            {/* ── Pruebas del Expediente (solo en la Audiencia OAP, incluida
                dentro de la vista fusionada CDE/OAP cuando aplica) — copia
                fiel de Expediente Junta Principal.dc.html ─────────────── */}
            {(etapaSeleccionada === 3 || isMergedView) && (() => {
              const totalActora = pruebasRegistro.filter((p) => p.parte === "actora").length;
              const totalDemandada = pruebasRegistro.filter((p) => p.parte === "demandada").length;
              const conteoAdmitidas = plural(pruebasRegistro.filter((p) => p.estatus === "admitida").length, "admitida", "admitidas");
              const conteoOfrecidas = plural(pruebasRegistro.filter((p) => p.estatus === "ofrecida").length, "ofrecida", "ofrecidas");
              const tabs = [
                { key: "actora", rol: "ACTORA", conteo: plural(totalActora, "prueba", "pruebas"), acento: "#0A539A" },
                { key: "demandada", rol: "DEMANDADA", conteo: plural(totalDemandada, "prueba", "pruebas"), acento: "#0A539A" },
              ];

              // Sub-pestañas por persona/razón social dentro del tab maestro activo
              const actoraLitigantes = actoresList.map((nombre) => ({
                id: nombre, nombre,
                conteo: pruebasRegistro.filter((p) => p.parte === "actora" && p.litigante === nombre).length,
              }));
              const demandadaLitigantes = demandadosList.map((d) => {
                const id = d.id_razon_social != null ? String(d.id_razon_social) : d.nombre;
                return {
                  id, nombre: d.nombre,
                  conteo: pruebasRegistro.filter((p) => p.parte === "demandada" && p.litigante === id).length,
                };
              });
              const litigantesActivos = partesTab === "actora" ? actoraLitigantes : demandadaLitigantes;

              const nombreLitigante = (p) => {
                if (p.parte === "actora") return p.litigante || (actorName || "—");
                const d = demandadosList.find((dd) => String(dd.id_razon_social) === String(p.litigante));
                return d?.nombre || p.litigante || (empresaName || "—");
              };

              const pieLinea = (p) => {
                const partes = [];
                partes.push("Ofrecida por " + nombreLitigante(p));
                if (p.fecha) partes.push(dayjs(p.fecha).format("DD [de] MMMM [de] YYYY"));
                if (p.observacion) partes.push(p.observacion);
                return partes.join(" · ");
              };

              const renderEditCard = () => (
                <div style={{ border: "1px solid #1b4bb8", borderRadius: 12, background: "#f8fafe", padding: "15px 16px" }}>
                  <div style={{ marginBottom: 14 }}>
                    <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", padding: "4px 9px", borderRadius: 6, background: "#1b4bb8", color: "#fff" }}>
                      {editingItem.isNew ? "Registrando prueba" : "Editando prueba"}
                    </span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
                    <span style={{ width: 20, height: 20, borderRadius: "50%", flex: "0 0 20px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, fontWeight: 700, color: "#fff", background: "#1b4bb8" }}>1</span>
                    <b style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: "#14306b" }}>Qué prueba es</b>
                  </div>
                  <div style={{ display: "grid", gap: 11, paddingLeft: 19, borderLeft: "2px solid #e6eaf2", marginLeft: 9, paddingBottom: 14 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 9.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#7a8699", marginBottom: 5 }}>Tipo de prueba</label>
                      <Select
                        style={{ width: "100%" }}
                        options={TIPO_PRUEBA_OPTIONS}
                        value={editingItem.tipo_prueba}
                        onChange={(v) => setEditingItem((s) => ({ ...s, tipo_prueba: v }))}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 9.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#7a8699", marginBottom: 5 }}>Objeto de la prueba o personas a cargo</label>
                      <Input.TextArea
                        rows={2}
                        value={editingItem.objeto}
                        onChange={(e) => setEditingItem((s) => ({ ...s, objeto: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
                    <span style={{ width: 20, height: 20, borderRadius: "50%", flex: "0 0 20px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, fontWeight: 700, color: "#fff", background: "#1b4bb8" }}>2</span>
                    <b style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: "#14306b" }}>Cuándo y con qué observación</b>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 11, marginLeft: 9, paddingLeft: 19 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 9.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#7a8699", marginBottom: 5 }}>Fecha</label>
                      <input
                        type="date"
                        value={editingItem.fecha || ""}
                        onChange={(e) => setEditingItem((s) => ({ ...s, fecha: e.target.value }))}
                        style={{ width: "100%", fontSize: 12.5, fontWeight: 500, color: "#1d2842", padding: "8px 10px", border: "1px solid #d7dded", borderRadius: 8, background: "#fff" }}
                      />
                    </div>
                    <div style={{ gridColumn: "1/-1" }}>
                      <label style={{ display: "block", fontSize: 9.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#7a8699", marginBottom: 5 }}>Observación</label>
                      <Input
                        placeholder="Objeciones, exhortos, acuerdos"
                        value={editingItem.observacion}
                        onChange={(e) => setEditingItem((s) => ({ ...s, observacion: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 9, alignItems: "center", paddingTop: 14, marginTop: 14, borderTop: "1px solid #e6eaf2", flexWrap: "wrap" }}>
                    <Button danger onClick={itemDelete} loading={savingItem}>Eliminar</Button>
                    <Button style={{ marginLeft: "auto" }} onClick={itemCancel}>Cancelar</Button>
                    <Button type="primary" onClick={itemSave} loading={savingItem}>Guardar prueba</Button>
                  </div>
                </div>
              );

              return (
                <div className="lxp-ep-partes-wrap">
                  <div style={{ display: "flex", alignItems: "center", gap: 11, paddingBottom: 15, marginBottom: 18, borderBottom: "1px solid #e6eaf2", flexWrap: "wrap" }}>
                    <div>
                      <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, letterSpacing: ".13em", textTransform: "uppercase", color: "#14306b" }}>Pruebas del expediente</h2>
                      <div style={{ fontSize: 11.5, color: "#7a8699", fontWeight: 400, marginTop: 3 }}>Ofrecidas en la tercera etapa de la audiencia trifásica — arts. 776 y siguientes, texto anterior</div>
                    </div>
                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", padding: "5px 10px", borderRadius: 6, background: "#dcfce7", border: "1px solid #86efac", color: "#16a34a" }}>{conteoAdmitidas}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", padding: "5px 10px", borderRadius: 6, background: "#fee2e2", border: "1px solid #fecaca", color: "#dc2626" }}>{conteoOfrecidas}</span>
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: "#7a8699" }}>Partes del juicio</span>
                    <span style={{ fontSize: 11, color: "#7a8699" }}>
                      {plural(actoresList.length || 1, "actor", "actores")} · {plural(demandadosList.length || 1, "demandada", "demandadas")}
                    </span>
                    <button
                      type="button"
                      onClick={litToggleOpen}
                      style={{ marginLeft: "auto", whiteSpace: "nowrap", border: "1px solid #d7dded", background: "#fff", color: "#1b4bb8", borderRadius: 8, fontFamily: "inherit", fontSize: 11.5, fontWeight: 600, padding: "6px 12px", cursor: "pointer" }}
                    >
                      {litOpen ? "Cerrar" : "Administrar partes"}
                    </button>
                  </div>

                  {litOpen && (() => {
                    const actoraCount = litRows.filter((r) => r.tipo === "actora").length;
                    const demandadaCount = litRows.filter((r) => r.tipo === "demandada").length;
                    const bloqueado = (row) =>
                      row.tipo === "actora"
                        ? (actoraCount < 2 || totalActora > 0)
                        : (demandadaCount < 2 || totalDemandada > 0);
                    const usedRazonIds = litRows.filter((r) => r.tipo === "demandada").map((r) => r.id_razon_social);

                    return (
                      <div style={{ border: "1px solid #cfdcfa", borderRadius: 12, background: "#f8fafe", padding: "16px 18px", marginBottom: 14 }}>
                        <div style={{ fontSize: 10.5, color: "#7a8699", marginBottom: 12 }}>
                          Registra a cada actor y a cada demandada del juicio. Cada parte tiene su propia pestaña con pruebas ofrecidas y admitidas.
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                          {litRows.map((row) => (
                            <div key={row._id} style={{ display: "grid", gridTemplateColumns: "minmax(200px,1fr) 150px 92px", gap: 9, alignItems: "end" }}>
                              <div>
                                <label style={{ display: "block", fontSize: 9.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#7a8699", marginBottom: 5 }}>Nombre o razón social</label>
                                {row.tipo === "actora" ? (
                                  <Input
                                    value={row.nombre}
                                    onChange={(e) => litSetRow(row._id, { nombre: e.target.value })}
                                  />
                                ) : (
                                  <Select
                                    showSearch
                                    optionFilterProp="label"
                                    style={{ width: "100%" }}
                                    placeholder="Selecciona razón social"
                                    options={todasRazonesOptions.filter((o) => o.value === row.id_razon_social || !usedRazonIds.includes(o.value))}
                                    value={row.id_razon_social ?? undefined}
                                    onChange={(v) => litSetRow(row._id, { id_razon_social: v ?? null })}
                                  />
                                )}
                              </div>
                              <div>
                                <label style={{ display: "block", fontSize: 9.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#7a8699", marginBottom: 5 }}>Carácter</label>
                                <Select
                                  style={{ width: "100%" }}
                                  value={row.tipo}
                                  options={[{ value: "actora", label: "Actora" }, { value: "demandada", label: "Demandada" }]}
                                  onChange={(v) => litSetRow(row._id, { tipo: v, nombre: v === "actora" ? row.nombre : "", id_razon_social: null })}
                                />
                              </div>
                              <button
                                type="button"
                                disabled={bloqueado(row)}
                                onClick={() => litRemoveRow(row._id)}
                                style={{ whiteSpace: "nowrap", border: "1px solid " + (bloqueado(row) ? "#e6eaf2" : "#fecdc9"), background: "#fff", color: bloqueado(row) ? "#a8b0bf" : "#d92d20", borderRadius: 8, fontFamily: "inherit", fontSize: 11.5, fontWeight: 600, padding: "8px 10px", cursor: bloqueado(row) ? "default" : "pointer" }}
                              >
                                {bloqueado(row) ? "Con pruebas" : "Quitar"}
                              </button>
                            </div>
                          ))}
                        </div>

                        <div style={{ paddingTop: 12, marginTop: 9, borderTop: "1px dashed #d7dded" }}>
                          <button
                            type="button"
                            onClick={litAddRow}
                            style={{ whiteSpace: "nowrap", border: "1px dashed #1b4bb8", background: "#fff", color: "#1b4bb8", borderRadius: 8, fontFamily: "inherit", fontSize: 12, fontWeight: 600, padding: "8px 13px", cursor: "pointer" }}
                          >
                            + Agregar otra parte
                          </button>
                        </div>

                        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 14, marginTop: 14, borderTop: "1px solid #e6eaf2" }}>
                          <Button onClick={() => setLitOpen(false)}>Cancelar</Button>
                          <Button type="primary" loading={savingPartes} onClick={litSave}>Guardar partes</Button>
                        </div>
                      </div>
                    );
                  })()}

                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", borderBottom: "1px solid #d7dded" }}>
                    {tabs.map((t) => {
                      const on = t.key === partesTab;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setPartesTab(t.key)}
                          style={{ display: "flex", alignItems: "center", gap: 9, whiteSpace: "nowrap", border: "1px solid " + (on ? t.acento : "#d7dded"), background: on ? t.acento : "#fff", color: on ? "#fff" : "#1d2842", borderRadius: "10px 10px 0 0", padding: "9px 15px", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, cursor: "pointer", marginBottom: -1, textAlign: "left" }}
                        >
                          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase" }}>{t.rol}</span>
                          <span style={{ fontSize: 10, fontWeight: 600, whiteSpace: "nowrap", color: on ? "rgba(255,255,255,.85)" : "#7a8699" }}>{t.conteo}</span>
                        </button>
                      );
                    })}
                  </div>

                  {litigantesActivos.length > 0 && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "10px 14px", borderLeft: "1px solid #d7dded", borderRight: "1px solid #d7dded", background: "#fbfcfe" }}>
                      {litigantesActivos.map((li) => {
                        const on = li.id === selectedLitigante;
                        return (
                          <button
                            key={li.id}
                            type="button"
                            onClick={() => setSelectedLitigante(li.id)}
                            style={{ display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap", border: "1px solid " + (on ? "#5CAFC5" : "#d7dded"), background: on ? "#5CAFC5" : "#fff", color: on ? "#fff" : "#1d2842", borderRadius: 999, padding: "6px 12px", fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                          >
                            <span style={{ maxWidth: 190, overflow: "hidden", textOverflow: "ellipsis" }}>{li.nombre}</span>
                            <span style={{ fontSize: 10, fontWeight: 600, color: on ? "rgba(255,255,255,.85)" : "#7a8699" }}>{plural(li.conteo, "prueba", "pruebas")}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div style={{ border: "1px solid #d7dded", borderTop: 0, borderRadius: "0 0 14px 14px", background: "#fff", padding: "18px 18px 20px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 16, alignItems: "start" }}>
                      {PASO_DEFS.map((paso) => {
                        const items = pruebasRegistro.filter((p) => p.parte === partesTab && p.litigante === selectedLitigante && p.estatus === paso.key);
                        const isAddingHere = editingItem?.isNew && editingItem.parte === partesTab && editingItem.litigante === selectedLitigante && editingItem.estatus === paso.key;
                        return (
                          <div key={paso.key} style={{ borderLeft: `3px solid ${paso.color}`, background: paso.key === "ofrecida" ? "#fef2f2" : "#fafefb", borderRadius: "0 10px 10px 0", padding: "13px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "nowrap", marginBottom: 11 }}>
                              <span style={{ width: 20, height: 20, borderRadius: "50%", flex: "0 0 20px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, fontWeight: 700, color: "#fff", background: paso.color }}>{paso.n}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <b style={{ display: "block", fontSize: 11.5, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "#14306b" }}>{paso.titulo}</b>
                                <span style={{ display: "block", fontSize: 10.5, color: "#7a8699", marginTop: 2, whiteSpace: "nowrap" }}>{paso.pie}</span>
                              </div>
                              <button
                                type="button"
                                disabled={!selectedLitigante}
                                onClick={() => itemAdd(partesTab, paso.key, selectedLitigante)}
                                style={{ marginLeft: "auto", whiteSpace: "nowrap", border: "none", background: paso.color, color: "#fff", borderRadius: 8, fontFamily: "inherit", fontSize: 11.5, fontWeight: 600, padding: "6px 11px", cursor: selectedLitigante ? "pointer" : "default", opacity: selectedLitigante ? 1 : 0.5 }}
                              >
                                + Registrar
                              </button>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                              {items.map((p) => {
                                const isEditingThis = editingItem && !editingItem.isNew && editingItem.id === p.id_junta_prueba_registro;
                                if (isEditingThis) return <div key={p.id_junta_prueba_registro}>{renderEditCard()}</div>;
                                return (
                                  <div
                                    key={p.id_junta_prueba_registro}
                                    onClick={() => itemEdit(p)}
                                    style={{ border: "1px solid " + (paso.key === "ofrecida" ? "#fecaca" : "#86efac"), borderLeft: `3px solid ${paso.color}`, borderRadius: 12, background: "#fbfcfe", padding: "13px 15px", cursor: "pointer" }}
                                  >
                                    <b style={{ display: "block", fontSize: 11.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#14306b" }}>{p.tipo_prueba}</b>
                                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, lineHeight: 1.45, marginTop: 10 }}>
                                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#74a6c9", marginTop: 7, flex: "0 0 5px" }} />
                                      <div>
                                        {p.objeto || "Sin objeto registrado"}
                                        <span style={{ display: "block", fontSize: 11, color: "#7a8699", marginTop: 3 }}>{pieLinea(p)}</span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              {isAddingHere && renderEditCard()}
                              {items.length === 0 && !isAddingHere && (
                                <div style={{ border: "1px dashed #d7dded", borderRadius: 10, padding: "13px 15px", fontSize: 12, color: "#7a8699", textAlign: "center" }}>
                                  Sin pruebas {paso.key}s de esta parte
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
